import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EmailRecipient {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  metadata?: Record<string, any>;
}

interface EmailContent {
  subject: string;
  html_content: string;
  text_content?: string;
  from_name?: string;
  reply_to?: string;
}

interface SingleEmailParams {
  recipient: EmailRecipient;
  campaign_id: string;
  content: EmailContent;
}

interface BatchEmailParams {
  recipients: EmailRecipient[];
  campaign_id: string;
  content: EmailContent;
  template_variables?: Record<string, any>;
}

interface TestEmailParams {
  to: string;
  subject: string;
  html: string;
  previewText?: string;
}

export const useEmailSender = () => {
  const queryClient = useQueryClient();

  // Function to send a single email
  const sendSingleEmail = async ({ recipient, campaign_id, content }: SingleEmailParams) => {
    try {
      // 1. Create a send record in the database
      const { data: sendRecord, error: sendError } = await supabase
        .from('newsletter_sends')
        .insert({
          subscriber_id: recipient.id,
          campaign_id,
          email: recipient.email,
          subject: content.subject,
          status: 'queued'
        })
        .select()
        .single();

      if (sendError) throw sendError;

      // 2. Call the edge function to send the email
      const response = await supabase.functions.invoke('send-email', {
        body: {
          action: 'send_single',
          data: {
            send_id: sendRecord.id,
            subscriber_id: recipient.id,
            campaign_id,
            email: recipient.email,
            subject: content.subject,
            html_content: content.html_content,
            text_content: content.text_content,
            from_name: content.from_name,
            reply_to: content.reply_to
          }
        }
      });

      if (response.error) throw new Error(response.error.message);

      // 3. Update the send record with the message ID
      if (response.data?.results?.[0]?.message_id) {
        await supabase
          .from('newsletter_sends')
          .update({
            message_id: response.data.results[0].message_id,
            status: 'sent',
            sent_at: new Date().toISOString()
          })
          .eq('id', sendRecord.id);
      } else if (!response.data?.results?.[0]?.success) {
        // Update with error information
        await supabase
          .from('newsletter_sends')
          .update({
            status: 'failed',
            error_info: response.data?.results?.[0]?.error || 'Unknown error'
          })
          .eq('id', sendRecord.id);
        
        throw new Error(response.data?.results?.[0]?.error || 'Failed to send email');
      }

      return response.data;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  // Function to send batch emails
  const sendBatchEmails = async ({ recipients, campaign_id, content, template_variables }: BatchEmailParams) => {
    try {
      // 1. Create send records for all recipients
      const sendRecords = recipients.map(recipient => ({
        subscriber_id: recipient.id,
        campaign_id,
        email: recipient.email,
        subject: content.subject,
        status: 'queued'
      }));

      const { data: createdSends, error: sendError } = await supabase
        .from('newsletter_sends')
        .insert(sendRecords)
        .select();

      if (sendError) throw sendError;

      // 2. Prepare the batch data for the edge function
      const batchData = createdSends.map((send, index) => {
        const recipient = recipients[index];
        return {
          send_id: send.id,
          subscriber_id: recipient.id,
          campaign_id,
          email: recipient.email,
          subject: content.subject,
          html_content: content.html_content,
          text_content: content.text_content,
          from_name: content.from_name,
          reply_to: content.reply_to
        };
      });

      // 3. Call the edge function to send the batch
      const response = await supabase.functions.invoke('send-email', {
        body: {
          action: 'send_batch',
          data: batchData
        }
      });

      if (response.error) throw new Error(response.error.message);

      // 4. Update the send records with results
      if (response.data?.results) {
        for (const result of response.data.results) {
          if (result.success) {
            await supabase
              .from('newsletter_sends')
              .update({
                message_id: result.message_id,
                status: 'sent',
                sent_at: new Date().toISOString()
              })
              .eq('id', result.send_id);
          } else {
            await supabase
              .from('newsletter_sends')
              .update({
                status: 'failed',
                error_info: result.error || 'Unknown error'
              })
              .eq('id', result.send_id);
          }
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error sending batch emails:', error);
      throw error;
    }
  };

  // Add function to send test emails
  const sendTestEmail = async ({ to, subject, html, previewText }: TestEmailParams) => {
    try {
      // Call the edge function to send the test email
      const response = await supabase.functions.invoke('send-email', {
        body: {
          to,
          subject,
          html,
          preview: previewText,
          type: 'test'
        }
      });

      if (response.error) throw new Error(response.error.message);
      
      return response.data;
    } catch (error) {
      console.error('Error sending test email:', error);
      throw error;
    }
  };

  // Mutation for test email
  const testEmailMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: () => {
      toast.success('Test email sent successfully');
    },
    onError: (error) => {
      toast.error(`Failed to send test email: ${error.message}`);
    }
  });

  // Mutation for single email
  const singleEmailMutation = useMutation({
    mutationFn: sendSingleEmail,
    onSuccess: () => {
      toast.success('Email sent successfully');
      queryClient.invalidateQueries({ queryKey: ['newsletter_sends'] });
      queryClient.invalidateQueries({ queryKey: ['campaign_stats'] });
    },
    onError: (error) => {
      toast.error(`Failed to send email: ${error.message}`);
    }
  });

  // Mutation for batch emails
  const batchEmailMutation = useMutation({
    mutationFn: sendBatchEmails,
    onSuccess: (data) => {
      const successCount = data?.results?.filter(r => r.success).length || 0;
      const failCount = data?.results?.filter(r => !r.success).length || 0;
      
      if (failCount === 0) {
        toast.success(`Successfully sent ${successCount} emails`);
      } else {
        toast.success(`Sent ${successCount} emails, ${failCount} failed`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['newsletter_sends'] });
      queryClient.invalidateQueries({ queryKey: ['campaign_stats'] });
    },
    onError: (error) => {
      toast.error(`Failed to send batch emails: ${error.message}`);
    }
  });

  return {
    sendSingleEmail: singleEmailMutation.mutate,
    sendBatchEmails: batchEmailMutation.mutate,
    sendTestEmail: testEmailMutation.mutate,
    isSendingSingle: singleEmailMutation.isPending,
    isSendingBatch: batchEmailMutation.isPending,
    isSendingTest: testEmailMutation.isPending,
    singleEmailError: singleEmailMutation.error,
    batchEmailError: batchEmailMutation.error,
    testEmailError: testEmailMutation.error
  };
};
